/**
 * Slug normalisation + validation — the single definition shared by the admin
 * panel (panelis) and the API.
 *
 * Slugs end up in public URLs (/products/<slug>, /category/<slug>, …), so they
 * must be URL-safe: lowercase ASCII letters, digits and single hyphens. Before
 * this existed every editor had its own copy of a slugify() that was only ever
 * applied as a fallback when the field was left blank — anything the user typed
 * was saved verbatim, so "Sarkanvīni / 2020!" became a live URL.
 *
 * Latvian/Russian text is transliterated rather than stripped, so "Sarkanvīni"
 * becomes "sarkanvini" instead of losing characters.
 */

// Characters that NFD cannot decompose into base + combining mark.
const CHAR_MAP: Record<string, string> = {
  đ: "d",
  ð: "d",
  ø: "o",
  œ: "oe",
  æ: "ae",
  ß: "ss",
  ł: "l",
  þ: "th",
};

// Cyrillic → Latin, so Russian names produce a readable slug rather than "".
const CYRILLIC_MAP: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh",
  з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o",
  п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts",
  ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu",
  я: "ya",
};

/** Longest slug we accept. Matches what the storefront routes comfortably handle. */
export const SLUG_MAX_LENGTH = 200;

/** A valid slug: lowercase alphanumerics in hyphen-separated groups. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Turn arbitrary text into a valid slug. Always returns something that
 * satisfies SLUG_PATTERN, or "" when the input has no usable characters.
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[đðøœæßłþ]/g, (ch) => CHAR_MAP[ch] ?? ch)
    .replace(/[Ѐ-ӿ]/g, (ch) => CYRILLIC_MAP[ch] ?? "")
    // Decompose accented letters, then drop the combining marks: ī → i, ē → e.
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH)
    // Slicing mid-group can leave a trailing hyphen.
    .replace(/-+$/g, "");
}

/** True when `value` is already a well-formed slug. */
export function isValidSlug(value: string): boolean {
  return value.length > 0 && value.length <= SLUG_MAX_LENGTH && SLUG_PATTERN.test(value);
}

export type SlugProblem = "empty" | "too_long" | "invalid_chars";

/**
 * Why `value` is not a usable slug, or null when it is fine. The caller turns
 * this into a localized message — this package carries no translations.
 */
export function slugProblem(value: string): SlugProblem | null {
  const v = value.trim();
  if (!v) return "empty";
  if (v.length > SLUG_MAX_LENGTH) return "too_long";
  if (!SLUG_PATTERN.test(v)) return "invalid_chars";
  return null;
}
